import * as THREE from 'three'
import type { EventBus } from '@/runtime/EventBus'
import {
  type AssetKind,
  CameraMode,
  type CameraState,
  type PickQuery,
  type PickResult,
  type RectPickQuery,
  type ViewportRect,
} from '@/shared/types/core'
import type {
  CameraRig,
  PickingService,
  SpatialQueryService,
  ViewManagedCamera,
} from '@/shared/types/services'
import type { SpatialSurfaceHit } from '@/shared/types/spatial'
import type { ViewId } from '@/shared/types/view'
import { ndcToRay, screenToNDC } from '@/shared/utils'
import { assertValidPickMinDistance } from '@/shared/utils/pickDepth'

const CAMERA_STATE_EPSILON = 1e-6
const PICK_PLANE_DIRECTION_EPSILON = 1e-8

/** 原生相机控制可通过表面查询命中的渲染资产种类。 */
export const NATIVE_CONTROL_SURFACE_KINDS = [
  'pointCloud',
  'gaussian-splat',
  'mesh',
  'tiles',
] as const satisfies readonly AssetKind[]

/**
 * 从偏移后的相机 near 平面解析当前射线的最小拾取距离。
 *
 * @param camera - 当前活动相机
 * @param ray - 当前屏幕位置生成的原始世界射线
 * @param nearOffset - near 平面沿相机前向的额外世界坐标偏移
 * @returns 原始射线上的非负最小距离；射线无法与目标平面正向相交时返回 null
 */
export function resolveNativeControlPickMinDistance(
  camera: THREE.Camera,
  ray: THREE.Ray,
  nearOffset: number
): number | null {
  assertValidPickMinDistance(nearOffset, 'surfacePicking.nearOffset')
  if (
    !(camera instanceof THREE.PerspectiveCamera) &&
    !(camera instanceof THREE.OrthographicCamera)
  ) {
    return null
  }

  camera.updateMatrixWorld(true)
  const cameraPosition = camera.getWorldPosition(new THREE.Vector3())
  const cameraForward = camera.getWorldDirection(new THREE.Vector3()).normalize()
  const rayDirection = ray.direction.clone().normalize()
  const denominator = rayDirection.dot(cameraForward)
  if (!Number.isFinite(denominator) || denominator <= PICK_PLANE_DIRECTION_EPSILON) {
    return null
  }

  const rayOriginDepth = ray.origin.clone().sub(cameraPosition).dot(cameraForward)
  const distance = (camera.near + nearOffset - rayOriginDepth) / denominator
  return Number.isFinite(distance) ? Math.max(0, distance) : null
}

/**
 * 从屏幕坐标直接解析原生控制器查询的最小拾取距离。
 *
 * @param camera - 当前活动相机
 * @param screen - 与 viewport 同一坐标系的屏幕像素位置
 * @param viewport - 当前视图矩形
 * @param nearOffset - 可选 near 平面偏移；未配置时返回 undefined
 * @returns 最小拾取距离；未配置返回 undefined，无法生成有效射线返回 null
 */
export function resolveNativeControlPickMinDistanceAtScreen(
  camera: THREE.Camera,
  screen: THREE.Vector2,
  viewport: ViewportRect,
  nearOffset: number | undefined
): number | null | undefined {
  if (nearOffset === undefined) {
    return undefined
  }
  if (viewport.width <= 0 || viewport.height <= 0) {
    return null
  }

  const ray = ndcToRay(screenToNDC(screen, viewport), camera)
  return resolveNativeControlPickMinDistance(camera, ray, nearOffset)
}

/**
 * 判断缓存支点是否仍位于当前偏移 near 平面之后。
 *
 * @param camera - 当前活动相机
 * @param point - 缓存的世界坐标支点
 * @param nearOffset - 可选 near 平面偏移
 * @returns 未配置偏移或支点位于包含边界之后时返回 true
 */
export function isPointBeyondNativeControlPickPlane(
  camera: THREE.Camera,
  point: THREE.Vector3,
  nearOffset: number | undefined
): boolean {
  if (nearOffset === undefined) {
    return true
  }
  assertValidPickMinDistance(nearOffset, 'surfacePicking.nearOffset')
  if (
    !(camera instanceof THREE.PerspectiveCamera) &&
    !(camera instanceof THREE.OrthographicCamera)
  ) {
    return false
  }

  camera.updateMatrixWorld(true)
  const cameraPosition = camera.getWorldPosition(new THREE.Vector3())
  const cameraForward = camera.getWorldDirection(new THREE.Vector3()).normalize()
  const pointDepth = point.clone().sub(cameraPosition).dot(cameraForward)
  return pointDepth >= camera.near + nearOffset
}

/** 控制器使用的 picking 子集接口 */
export interface NativeControlPickingService {
  /**
   * 按查询条件执行拾取。
   *
   * @param query - 拾取查询
   * @returns 命中结果数组，按距离排序
   */
  pick(query: PickQuery): PickResult[]
  /** 异步点拾取；WebGPU 控制器在该入口上避免阻塞主线程。 */
  pickAsync?(query: PickQuery): Promise<PickResult[]>
  /**
   * 矩形区域拾取。
   *
   * @param query - 矩形查询
   * @returns 命中结果数组
   */
  pickRect(query: RectPickQuery): PickResult[]
  /** 异步矩形拾取。 */
  pickRectAsync?(query: RectPickQuery): Promise<PickResult[]>
  /**
   * 屏幕坐标拾取。
   *
   * @param screen - 屏幕坐标，单位像素
   * @param camera - 当前相机
   * @param options - 额外查询参数
   * @returns 命中结果数组
   */
  pickAtScreen(
    screen: THREE.Vector2,
    camera: THREE.Camera,
    options?: Partial<PickQuery>
  ): PickResult[]
  /** 异步屏幕拾取。 */
  pickAtScreenAsync?(
    screen: THREE.Vector2,
    camera: THREE.Camera,
    options?: Partial<PickQuery>
  ): Promise<PickResult[]>
}

/** 控制器表面查询结果 */
export interface NativeControlSurfaceQueryHit {
  /** 命中世界坐标 */
  point: THREE.Vector3
  /** 命中来源：spatial 查询或 picking 回退 */
  source: 'spatial' | 'picking'
  /** 来自 spatial 查询的命中详情 */
  spatialHit?: SpatialSurfaceHit
  /** 来自 picking 的命中详情 */
  pickResult?: PickResult
}

/** 控制器使用的表面查询子集接口 */
export interface NativeControlSurfaceQueryService {
  /**
   * 屏幕坐标表面查询。
   *
   * @param screen - 屏幕坐标，单位像素
   * @param camera - 当前相机
   * @param options - 额外查询参数
   * @returns 命中信息；未命中返回 null
   */
  queryAtScreen(
    screen: THREE.Vector2,
    camera: THREE.Camera,
    options?: Partial<PickQuery>
  ): NativeControlSurfaceQueryHit | null
  /** 异步屏幕表面查询；spatial 命中仍可同步短路。 */
  queryAtScreenAsync?(
    screen: THREE.Vector2,
    camera: THREE.Camera,
    options?: Partial<PickQuery>
  ): Promise<NativeControlSurfaceQueryHit | null>
}

/** 控制器运行所需的共享服务集合 */
export interface NativeControlServices {
  /** 所属视图 ID；单视口场景可省略 */
  viewId?: ViewId
  /** WebGPU 下优先使用异步拾取；缺省保持旧同步控制器行为。 */
  preferAsyncPicking?: boolean
  /** 相机 rig */
  rig: CameraRig
  /** 视口相机管理器 */
  viewportCamera: ViewManagedCamera
  /** 事件总线，用于广播相机/枢轴事件 */
  events?: EventBus
  /** 渲染器 DOM 元素，部分控制器需要与之交互 */
  rendererDomElement?: HTMLElement
  /**
   * 获取视口尺寸。
   *
   * @returns 视口的宽高（像素）
   */
  getViewportSize(): { width: number; height: number }
  /**
   * 获取视图矩形。
   *
   * @returns 视图在容器内的矩形
   */
  getViewRect?(): ViewportRect
  /** 绑定的 picking 服务 */
  picking?: NativeControlPickingService
  /** 绑定的表面查询服务 */
  surfaceQuery?: NativeControlSurfaceQueryService
  /** 绑定的空间查询服务，用于世界空间射线/碰撞查询。 */
  spatial?: SpatialQueryService
  /**
   * 获取当前场景包围盒。
   *
   * @param purpose - 用途：'default' 常规；'fit' 适配相机
   * @returns 包围盒
   */
  getSceneBounds?(purpose?: 'default' | 'fit'): THREE.Box3
}

/** 当前控制器是否应将可选的表面拾取迁移到异步入口。 */
export function shouldUseAsyncPicking(services: NativeControlServices | null | undefined): boolean {
  return services?.preferAsyncPicking === true && !!services.surfaceQuery?.queryAtScreenAsync
}

/** 场景尺度参与导航速度计算的默认比例。 */
export const DEFAULT_NAVIGATION_SCALE_SCENE_RATIO = 0.002
/** 控制器鼠标平移/滚轮使用的默认绝对尺度下限。 */
export const DEFAULT_NAVIGATION_SCALE_ABSOLUTE_MINIMUM = 0.02
/** 捕捉点滚轮缩放的默认最小步长比例。 */
export const DEFAULT_WHEEL_MIN_STEP_RATIO = 0.05
/** 捕捉点滚轮缩放的默认绝对步长下限；默认关闭固定世界单位下限。 */
export const DEFAULT_WHEEL_ABSOLUTE_MIN_STEP = 0
/** 像素模式下一个标准滚轮刻度对应的像素量。 */
export const DEFAULT_WHEEL_PIXEL_STEP = 100
/** 行模式下一个标准滚轮刻度对应的行数。 */
export const DEFAULT_WHEEL_LINE_STEP = 3
/** 单个滚轮事件和同帧累计允许的最大逻辑刻度。 */
export const DEFAULT_WHEEL_INPUT_LIMIT = 1
const WHEEL_DELTA_MODE_PIXEL = 0
const WHEEL_DELTA_MODE_LINE = 1
const WHEEL_DELTA_MODE_PAGE = 2
const DEFAULT_DOUBLE_CLICK_MIN_TARGET_DISTANCE = 0.2

/** 解析场景感知导航尺度的参数。 */
export interface ResolveNavigationScaleOptions {
  /** 当前相机到视线 target 的距离。 */
  cameraTargetDistance?: number | null
  /** 当前场景 bounds；缺失或为空时只使用距离和绝对下限。 */
  sceneBounds?: THREE.Box3 | null
  /** bounds 对角线转换为导航尺度下限的比例。 */
  sceneScaleRatio?: number
  /** 绝对导航尺度下限。 */
  absoluteMinimum?: number
}

/** 捕捉点滚轮步进的参数。 */
export interface ResolvePickedPointWheelStepOptions {
  /** 当前相机位置。 */
  position: THREE.Vector3
  /** 当前视线 target。 */
  target: THREE.Vector3
  /** 当前视线方向。 */
  forward: THREE.Vector3
  /** 滚轮命中的世界点。 */
  pickedPoint: THREE.Vector3
  /** 上一帧尚未完全消耗的滚轮缩放位移。 */
  currentZoomDelta?: THREE.Vector3 | null
  /** 累计滚轮输入，正数向前。 */
  wheelDelta: number
  /** 远距离时相对剩余距离的步长比例。 */
  proportionalStepRatio: number
  /** 场景感知导航尺度。 */
  navigationScale: number
  /** 近距离时相对导航尺度的最小步长比例。 */
  minStepRatio?: number
  /** 近距离时绝对最小步长。 */
  absoluteMinStep?: number
}

export interface ResolveDoubleClickWheelDeltaOptions {
  position: THREE.Vector3
  pickedPoint: THREE.Vector3
  hit?: NativeControlSurfaceQueryHit | null
  proportionalStepRatio: number
  navigationScale: number
  minStepRatio?: number
  absoluteMinStep?: number
}

/** 捕捉点滚轮步进的完整状态转移结果。 */
export interface PickedPointWheelStepResult {
  /** 下一帧应写入 rig 的相机位置。 */
  nextPosition: THREE.Vector3
  /** 下一帧应写入 rig/navTarget 的视线 target。 */
  nextTarget: THREE.Vector3
  /** 下一帧继续衰减的滚轮缩放位移。 */
  nextZoomDelta: THREE.Vector3
  /** 下一帧剩余的捕捉点缩放距离；穿越捕捉点后为 null。 */
  nextZoomDistance: number | null
  /** 本次滚轮步进是否已经穿越捕捉点。 */
  crossedPickedPoint: boolean
}

/** 无捕捉点滚轮自由前进的参数。 */
export interface ResolveWheelForwardStepOptions {
  /** 当前相机到视线 target 的距离。 */
  cameraTargetDistance: number
  /** 累计滚轮输入，正数向前。 */
  wheelDelta: number
  /** 远距离时相对 target 距离的步长比例。 */
  proportionalStepRatio: number
  /** 场景感知导航尺度。 */
  navigationScale: number
  /** 近距离时相对导航尺度的最小步长比例。 */
  minStepRatio?: number
  /** 近距离时绝对最小步长。 */
  absoluteMinStep?: number
}

/**
 * 将浏览器滚轮输入归一化为向前为正的逻辑刻度。
 *
 * @param deltaY - WheelEvent.deltaY
 * @param deltaMode - WheelEvent.deltaMode
 * @param limit - 单事件最大逻辑刻度
 * @returns 位于 [-limit, limit] 的逻辑刻度
 */
export function resolveWheelInputDelta(
  deltaY: number,
  deltaMode = WHEEL_DELTA_MODE_PIXEL,
  limit = DEFAULT_WHEEL_INPUT_LIMIT
): number {
  if (!Number.isFinite(deltaY) || deltaY === 0) {
    return 0
  }

  const safeLimit = toFinitePositive(limit)
  if (safeLimit === 0) {
    return 0
  }

  let stepSize = DEFAULT_WHEEL_PIXEL_STEP
  if (deltaMode === WHEEL_DELTA_MODE_LINE) {
    stepSize = DEFAULT_WHEEL_LINE_STEP
  } else if (deltaMode === WHEEL_DELTA_MODE_PAGE) {
    stepSize = 1
  }

  return THREE.MathUtils.clamp(-deltaY / stepSize, -safeLimit, safeLimit)
}

/**
 * 累计同一渲染帧内的滚轮逻辑刻度并限幅。
 *
 * @param current - 当前帧已累计刻度
 * @param input - 本次输入刻度
 * @param limit - 当前帧最大累计刻度
 * @returns 限幅后的累计刻度
 */
export function accumulateWheelInputDelta(
  current: number,
  input: number,
  limit = DEFAULT_WHEEL_INPUT_LIMIT
): number {
  const safeLimit = toFinitePositive(limit)
  if (safeLimit === 0) {
    return 0
  }

  const safeCurrent = Number.isFinite(current) ? current : 0
  const safeInput = Number.isFinite(input) ? input : 0
  return THREE.MathUtils.clamp(safeCurrent + safeInput, -safeLimit, safeLimit)
}

/**
 * 用尺寸构造以 (0,0) 为原点的视口矩形。
 *
 * @param size - 宽高，单位像素
 * @returns 视口矩形
 */
export function createViewportRect(size: { width: number; height: number }): ViewportRect {
  return {
    x: 0,
    y: 0,
    width: size.width,
    height: size.height,
  }
}

/**
 * 解析控制器共享的场景感知导航尺度。
 *
 * @param options - 当前相机距离、场景 bounds 和下限参数
 * @returns 不低于 camera-target、bounds 对角线比例和绝对下限的导航尺度
 */
export function resolveNavigationScale(options: ResolveNavigationScaleOptions): number {
  const sceneScaleRatio = options.sceneScaleRatio ?? DEFAULT_NAVIGATION_SCALE_SCENE_RATIO
  const absoluteMinimum = options.absoluteMinimum ?? DEFAULT_NAVIGATION_SCALE_ABSOLUTE_MINIMUM
  const distance = toFinitePositive(options.cameraTargetDistance)
  const sceneScale = resolveSceneBoundsScale(options.sceneBounds, sceneScaleRatio)
  return Math.max(distance, sceneScale, toFinitePositive(absoluteMinimum))
}

/**
 * 计算捕捉点滚轮缩放的下一步状态。
 *
 * @param options - 当前相机状态、捕捉点和步长参数
 * @returns 完整滚轮状态转移结果
 */
export function resolvePickedPointWheelStep(
  options: ResolvePickedPointWheelStepOptions
): PickedPointWheelStepResult {
  const zoomDelta = options.currentZoomDelta ?? new THREE.Vector3()
  const resolvedPosition = options.position.clone().add(zoomDelta)
  const wheelSign = Math.sign(options.wheelDelta)
  if (wheelSign === 0) {
    return {
      nextPosition: resolvedPosition.clone(),
      nextTarget: options.target.clone(),
      nextZoomDelta: zoomDelta.clone(),
      nextZoomDistance: resolvedPosition.distanceTo(options.pickedPoint),
      crossedPickedPoint: false,
    }
  }

  const pickedPointDirection = resolveSafeDirection(
    options.pickedPoint.clone().sub(options.position),
    options.forward
  )
  const forward = resolveSafeDirection(options.forward, pickedPointDirection)
  const distanceToPickedPoint = Math.max(resolvedPosition.distanceTo(options.pickedPoint), 1e-9)
  const stepDistance = resolveWheelStepMagnitude({
    baseDistance: distanceToPickedPoint,
    wheelMagnitude: Math.abs(options.wheelDelta),
    proportionalStepRatio: options.proportionalStepRatio,
    navigationScale: options.navigationScale,
    minStepRatio: options.minStepRatio,
    absoluteMinStep: options.absoluteMinStep,
  })
  const signedStep = wheelSign * stepDistance
  const forwardDistanceToPickedPoint = options.pickedPoint
    .clone()
    .sub(resolvedPosition)
    .dot(forward)
  const crossedPickedPoint =
    wheelSign > 0 &&
    (distanceToPickedPoint <= stepDistance + 1e-9 || forwardDistanceToPickedPoint <= 0)

  if (crossedPickedPoint) {
    const nextPosition = resolvedPosition.clone().addScaledVector(forward, stepDistance)
    const followDistance = Math.max(
      options.position.distanceTo(options.target),
      resolveMinimumWheelStep(
        options.navigationScale,
        options.minStepRatio,
        options.absoluteMinStep
      )
    )
    const nextTarget = nextPosition.clone().addScaledVector(forward, followDistance)
    return {
      nextPosition,
      nextTarget,
      nextZoomDelta: new THREE.Vector3(),
      nextZoomDistance: null,
      crossedPickedPoint: true,
    }
  }

  const nextPosition = resolvedPosition.clone().addScaledVector(pickedPointDirection, signedStep)
  const nextZoomDistance = nextPosition.distanceTo(options.pickedPoint)
  return {
    nextPosition,
    nextTarget: nextPosition.clone().addScaledVector(forward, nextZoomDistance),
    nextZoomDelta: nextPosition.clone().sub(options.position),
    nextZoomDistance,
    crossedPickedPoint: false,
  }
}

/**
 * 计算未命中捕捉点时沿视线方向的滚轮移动距离。
 *
 * @param options - 当前距离、滚轮输入和共享步长参数
 * @returns 带符号的视线方向移动距离
 */
export function resolveWheelForwardStep(options: ResolveWheelForwardStepOptions): number {
  const wheelSign = Math.sign(options.wheelDelta)
  if (wheelSign === 0) {
    return 0
  }

  return (
    wheelSign *
    resolveWheelStepMagnitude({
      baseDistance: options.cameraTargetDistance,
      wheelMagnitude: Math.abs(options.wheelDelta),
      proportionalStepRatio: options.proportionalStepRatio,
      navigationScale: options.navigationScale,
      minStepRatio: options.minStepRatio,
      absoluteMinStep: options.absoluteMinStep,
    })
  )
}

export function resolveDoubleClickWheelDelta(options: ResolveDoubleClickWheelDeltaOptions): number {
  const distance = options.position.distanceTo(options.pickedPoint)
  if (!Number.isFinite(distance) || distance <= 1e-9) {
    return 0
  }

  const targetDistance = Math.min(
    distance,
    Math.max(
      resolveSurfaceHitRadius(options.hit),
      DEFAULT_DOUBLE_CLICK_MIN_TARGET_DISTANCE,
      resolveMinimumWheelStep(
        options.navigationScale,
        options.minStepRatio,
        options.absoluteMinStep
      )
    )
  )
  const moveDistance = distance - targetDistance
  if (moveDistance <= 1e-9) {
    return 0
  }

  const denominator = Math.max(distance * options.proportionalStepRatio, 1e-9)
  return moveDistance / denominator
}

export function resolveDoubleClickTargetRadius(
  hit: NativeControlSurfaceQueryHit | null | undefined,
  navigationScale: number,
  minStepRatio?: number,
  absoluteMinStep?: number
): number {
  return Math.max(
    resolveSurfaceHitRadius(hit),
    DEFAULT_DOUBLE_CLICK_MIN_TARGET_DISTANCE,
    resolveMinimumWheelStep(navigationScale, minStepRatio, absoluteMinStep)
  )
}

/**
 * 构造仅作用于指定视图的 picking 子集，自动追加 viewId/viewRect 等作用域字段。
 *
 * @param viewId - 作用的视图 ID
 * @param getViewRect - 获取视图局部矩形的回调
 * @param getViewClientRect - 获取视图客户端矩形（含容器偏移）的回调
 * @param picking - 全局 picking 服务
 * @returns 视图作用域的 NativeControlPickingService
 */
export function createViewLocalPickingService(
  viewId: ViewId,
  getViewRect: () => ViewportRect,
  getViewClientRect: () => ViewportRect,
  picking: PickingService
): NativeControlPickingService {
  const applyViewScope = <T extends Partial<PickQuery> | Partial<RectPickQuery>>(
    query: T
  ): T & { viewId: ViewId; viewRect: ViewportRect; viewport: ViewportRect } => {
    const viewRect = cloneViewportRect(getViewRect())
    return {
      ...query,
      viewId,
      viewRect,
      viewport: cloneViewportRect(viewRect),
    }
  }

  return {
    pick: (query) => picking.pick(applyViewScope(query)),
    pickAsync: (query) => picking.pickAsync(applyViewScope(query)),
    pickRect: (query) => picking.pickRect(applyViewScope(query)),
    pickRectAsync: (query) => picking.pickRectAsync(applyViewScope(query)),
    pickAtScreen: (screen, camera, options = {}) =>
      picking.pickAtScreen(
        toContainerLocalScreen(screen, getViewRect(), getViewClientRect()),
        camera,
        applyViewScope(options)
      ),
    pickAtScreenAsync: (screen, camera, options = {}) =>
      picking.pickAtScreenAsync(
        toContainerLocalScreen(screen, getViewRect(), getViewClientRect()),
        camera,
        applyViewScope(options)
      ),
  }
}

/**
 * 构造仅作用于指定视图的表面查询服务，优先走 spatial，失败回退到 picking。
 *
 * @param viewId - 作用的视图 ID
 * @param getViewRect - 获取视图局部矩形的回调
 * @param getViewClientRect - 获取视图客户端矩形的回调
 * @param picking - 全局 picking 服务
 * @param spatial - 空间查询服务；为空则直接使用 picking
 * @returns 视图作用域的 NativeControlSurfaceQueryService
 */
export function createViewLocalSurfaceQueryService(
  viewId: ViewId,
  getViewRect: () => ViewportRect,
  getViewClientRect: () => ViewportRect,
  picking: PickingService,
  spatial?: SpatialQueryService
): NativeControlSurfaceQueryService {
  const applyViewScope = <T extends Partial<PickQuery>>(
    query: T
  ): T & { viewId: ViewId; viewRect: ViewportRect; viewport: ViewportRect } => {
    const viewRect = cloneViewportRect(getViewRect())
    return {
      ...query,
      viewId,
      viewRect,
      viewport: cloneViewportRect(viewRect),
    }
  }

  return {
    queryAtScreen: (screen, camera, options = {}) => {
      const scopedOptions = applyViewScope(options)
      const localScreen = toContainerLocalScreen(screen, getViewRect(), getViewClientRect())

      if (spatial) {
        const spatialHit = spatial.raycastSurfaceAtScreen({
          surfaceUsage: 'camera-control',
          camera,
          screen: localScreen,
          viewId: scopedOptions.viewId,
          viewRect: scopedOptions.viewRect,
          viewport: scopedOptions.viewport,
          layerMask: scopedOptions.layerMask,
          minDistance: scopedOptions.minDistance,
          ownerAssetId: scopedOptions.assetIds?.[0],
          ownerKind: scopedOptions.kinds?.length === 1 ? scopedOptions.kinds[0] : undefined,
        })

        if (spatialHit) {
          return {
            point: spatialHit.point.clone(),
            source: 'spatial',
            spatialHit,
          }
        }
      }

      const [pickResult] = picking.pickAtScreen(localScreen, camera, scopedOptions)
      if (!pickResult) {
        return null
      }

      return {
        point: pickResult.point.clone(),
        source: 'picking',
        pickResult,
      }
    },
    queryAtScreenAsync: async (screen, camera, options = {}) => {
      const scopedOptions = applyViewScope(options)
      const localScreen = toContainerLocalScreen(screen, getViewRect(), getViewClientRect())

      if (spatial) {
        const spatialHit = spatial.raycastSurfaceAtScreen({
          surfaceUsage: 'camera-control',
          camera,
          screen: localScreen,
          viewId: scopedOptions.viewId,
          viewRect: scopedOptions.viewRect,
          viewport: scopedOptions.viewport,
          layerMask: scopedOptions.layerMask,
          minDistance: scopedOptions.minDistance,
          ownerAssetId: scopedOptions.assetIds?.[0],
          ownerKind: scopedOptions.kinds?.length === 1 ? scopedOptions.kinds[0] : undefined,
        })

        if (spatialHit) {
          return {
            point: spatialHit.point.clone(),
            source: 'spatial' as const,
            spatialHit,
          }
        }
      }

      const [pickResult] = await picking.pickAtScreenAsync(localScreen, camera, scopedOptions)
      if (!pickResult) return null
      return {
        point: pickResult.point.clone(),
        source: 'picking' as const,
        pickResult,
      }
    },
  }
}

/**
 * 取得 ViewManagedCamera 当前激活的具体相机。
 *
 * @param viewportCamera - 视口相机管理器
 * @returns 透视或正交相机
 */
export function getActiveManagedCamera(
  viewportCamera: ViewManagedCamera
): THREE.PerspectiveCamera | THREE.OrthographicCamera {
  return viewportCamera.getActiveCamera() as THREE.PerspectiveCamera | THREE.OrthographicCamera
}

/**
 * 将 rig 状态同步到 ViewportCamera 并返回活动相机。
 *
 * @param services - 控制器共享服务
 * @returns 同步后的活动相机
 */
export function syncViewportCameraFromRig(
  services: NativeControlServices
): THREE.PerspectiveCamera | THREE.OrthographicCamera {
  const viewport = services.getViewRect?.() ?? createViewportRect(services.getViewportSize())
  services.viewportCamera.syncFromRig(
    services.rig.getState(),
    services.rig.near,
    services.rig.far,
    viewport
  )
  return getActiveManagedCamera(services.viewportCamera)
}

/**
 * 判断异步拾取请求开始时捕获的相机是否仍是当前 View 的活动相机。
 *
 * 相机被替换后，旧请求的结果不能再回写控制器状态；这里只做身份判断，
 * 不同步 rig，也不触碰当前相机姿态。
 */
export function isCurrentViewportCamera(
  services: NativeControlServices,
  camera: THREE.Camera
): boolean {
  return services.viewportCamera.getActiveCamera() === camera
}

/**
 * 将相机姿态与 target 写回 rig。
 *
 * @param services - 控制器共享服务
 * @param camera - 当前相机
 * @param target - 视线目标点
 */
export function syncRigFromCamera(
  services: NativeControlServices,
  camera: THREE.PerspectiveCamera | THREE.OrthographicCamera,
  target: THREE.Vector3
): void {
  const previous = services.rig.getState()
  const position = new THREE.Vector3().copy(camera.position)
  const up = new THREE.Vector3().copy(camera.up)

  const nextState: CameraState = {
    ...previous,
    position,
    target: new THREE.Vector3().copy(target),
    up,
    fov: camera instanceof THREE.PerspectiveCamera ? camera.fov : previous.fov,
    zoom: camera instanceof THREE.OrthographicCamera ? camera.zoom : previous.zoom,
    mode:
      camera instanceof THREE.OrthographicCamera ? CameraMode.Orthographic : CameraMode.Perspective,
  }

  if (isCameraStateEquivalent(previous, nextState)) {
    return
  }

  services.rig.setState(nextState)
}

function isCameraStateEquivalent(a: CameraState, b: CameraState): boolean {
  return (
    a.position.distanceToSquared(b.position) <= CAMERA_STATE_EPSILON * CAMERA_STATE_EPSILON &&
    a.target.distanceToSquared(b.target) <= CAMERA_STATE_EPSILON * CAMERA_STATE_EPSILON &&
    a.up.distanceToSquared(b.up) <= CAMERA_STATE_EPSILON * CAMERA_STATE_EPSILON &&
    Math.abs(a.fov - b.fov) <= CAMERA_STATE_EPSILON &&
    Math.abs(a.zoom - b.zoom) <= CAMERA_STATE_EPSILON &&
    a.mode === b.mode
  )
}

/**
 * 按朝向向量派生 target 并同步到 rig。
 *
 * @param services - 控制器共享服务
 * @param camera - 当前相机
 * @param direction - 视线方向（将被归一化）
 */
export function syncRigFromDirection(
  services: NativeControlServices,
  camera: THREE.PerspectiveCamera | THREE.OrthographicCamera,
  direction: THREE.Vector3
): void {
  const distance = Math.max(services.rig.position.distanceTo(services.rig.target), 1)
  const target = new THREE.Vector3()
    .copy(camera.position)
    .addScaledVector(direction.clone().normalize(), distance)

  syncRigFromCamera(services, camera, target)
}

/**
 * 计算消除 roll 的 up 向量。
 *
 * @param forward - 视线方向
 * @param preferredUp - 参考向上方向，默认 (0, 0, 1)
 * @returns 与 forward 正交且贴近参考方向的 up 向量
 */
export function computeRollFreeUp(
  forward: THREE.Vector3,
  preferredUp = new THREE.Vector3(0, 0, 1)
): THREE.Vector3 {
  const normalizedForward = forward.clone().normalize()
  const referenceUp = preferredUp.clone().normalize()

  if (normalizedForward.lengthSq() < 1e-8) {
    return referenceUp
  }

  if (Math.abs(normalizedForward.dot(referenceUp)) > 0.999) {
    referenceUp.set(0, 1, 0)
    if (Math.abs(normalizedForward.dot(referenceUp)) > 0.999) {
      referenceUp.set(1, 0, 0)
    }
  }

  const side = new THREE.Vector3().crossVectors(normalizedForward, referenceUp).normalize()

  return new THREE.Vector3().crossVectors(side, normalizedForward).normalize()
}

function cloneViewportRect(rect: ViewportRect): ViewportRect {
  return {
    x: rect.x,
    y: rect.y,
    width: rect.width,
    height: rect.height,
  }
}

function resolveSurfaceHitRadius(hit: NativeControlSurfaceQueryHit | null | undefined): number {
  return Math.max(
    resolvePickResultRadius(hit?.pickResult),
    resolveSpatialHitRadius(hit?.spatialHit)
  )
}

function resolvePickResultRadius(result: PickResult | null | undefined): number {
  const raw = result?._raw as any
  return Math.max(
    resolvePointCloudNodeRadius(raw?.__node ?? raw?.node),
    resolveGsplatInfoRadius(raw?.splatInfo)
  )
}

function resolveSpatialHitRadius(hit: SpatialSurfaceHit | null | undefined): number {
  const raw = hit?.raw as any
  return Math.max(
    resolvePointCloudNodeRadius(raw?.__node ?? raw?.node),
    resolveGsplatInfoRadius(raw?.splatInfo)
  )
}

function resolvePointCloudNodeRadius(node: any): number {
  const sphere = node?.getBoundingSphere?.()
  if (!sphere || !Number.isFinite(sphere.radius) || sphere.radius <= 0) {
    return 0
  }

  const worldSphere = sphere.clone()
  const matrixWorld = node?.sceneNode?.matrixWorld ?? node?.matrixWorld
  if (matrixWorld?.isMatrix4) {
    worldSphere.applyMatrix4(matrixWorld)
  }
  return toFinitePositive(worldSphere.radius)
}

function resolveGsplatInfoRadius(splatInfo: any): number {
  const box = new THREE.Box3()
  if (
    typeof splatInfo?.getWorldAabb === 'function' &&
    splatInfo.getWorldAabb(box) &&
    !box.isEmpty()
  ) {
    return toFinitePositive(box.getBoundingSphere(new THREE.Sphere()).radius)
  }

  const placementBox = splatInfo?.placement?.getWorldAabb?.()
  if (placementBox instanceof THREE.Box3 && !placementBox.isEmpty()) {
    return toFinitePositive(placementBox.getBoundingSphere(new THREE.Sphere()).radius)
  }

  return 0
}

function toContainerLocalScreen(
  screen: THREE.Vector2,
  viewRect: ViewportRect,
  viewClientRect: ViewportRect
): THREE.Vector2 {
  return new THREE.Vector2(
    screen.x - viewClientRect.x + viewRect.x,
    screen.y - viewClientRect.y + viewRect.y
  )
}

function resolveSceneBoundsScale(
  sceneBounds: THREE.Box3 | null | undefined,
  sceneScaleRatio: number
): number {
  if (!sceneBounds || sceneBounds.isEmpty()) {
    return 0
  }

  const diagonal = sceneBounds.getSize(new THREE.Vector3()).length()
  if (!Number.isFinite(diagonal) || diagonal <= 0) {
    return 0
  }

  return diagonal * Math.max(sceneScaleRatio, 0)
}

function resolveWheelStepMagnitude(options: {
  baseDistance: number
  wheelMagnitude: number
  proportionalStepRatio: number
  navigationScale: number
  minStepRatio?: number
  absoluteMinStep?: number
}): number {
  const wheelMagnitude = toFinitePositive(options.wheelMagnitude)
  if (wheelMagnitude <= 0) {
    return 0
  }

  const proportionalStep =
    toFinitePositive(options.baseDistance) *
    Math.max(options.proportionalStepRatio, 0) *
    wheelMagnitude
  const minimumStep =
    resolveMinimumWheelStep(
      options.navigationScale,
      options.minStepRatio,
      options.absoluteMinStep
    ) * wheelMagnitude

  return Math.max(proportionalStep, minimumStep)
}

function resolveMinimumWheelStep(
  navigationScale: number,
  minStepRatio = DEFAULT_WHEEL_MIN_STEP_RATIO,
  absoluteMinStep = DEFAULT_WHEEL_ABSOLUTE_MIN_STEP
): number {
  return Math.max(
    toFinitePositive(navigationScale) * Math.max(minStepRatio, 0),
    toFinitePositive(absoluteMinStep)
  )
}

function resolveSafeDirection(preferred: THREE.Vector3, fallback: THREE.Vector3): THREE.Vector3 {
  if (preferred.lengthSq() > 1e-12) {
    return preferred.normalize()
  }

  if (fallback.lengthSq() > 1e-12) {
    return fallback.clone().normalize()
  }

  return new THREE.Vector3(0, 1, 0)
}

function toFinitePositive(value: number | null | undefined): number {
  if (value == null || !Number.isFinite(value) || value <= 0) {
    return 0
  }

  return value
}
